// Demo data for VAT, year end, statements, recurring, users, import and
// backups. Each follows the rule the database enforces, so the screens show
// the same refusals they will meet against the API.
import { addMoney, compareMoney, negate, toUnits } from '@elysian/ui';
import { unmatchedBankLines } from './mockBank';
import { demoDocuments, demoStore } from './mockDocuments';
import { openInboxCount } from './mockInbox';
import { recordDemoAudit } from './mockAudit';
import type { OpenItemsCheck, SageCompare, SageCompareAged, SageCompareRow } from './source';
import type {
  AccountMapEntry, Backup, ChecklistItem, DocumentInput, FiscalYear, ImportBatch, ImportEntity, LedgerSource, MemberList,
  OpeningBalanceStatus, Period, RecurringRun, RecurringTemplate, RosVat3, SageStatus, StatementSchedule, VatReturn,
} from './source';

type SettingsSource = Pick<LedgerSource,
  'vatReturns' | 'calculateVat' | 'rosVat3' | 'rosVat3Csv' | 'fileVat' | 'fiscalYears' | 'yearEndChecklist' | 'closeYear'
  | 'reopenYear' | 'setPeriodStatus' | 'monthEndChecklist' | 'closePeriod' | 'createStatements' | 'statementSchedules' | 'createStatementSchedule' | 'recurring'
  | 'createRecurring' | 'runRecurringNow' | 'recurringRuns' | 'approveRun' | 'declineRun' | 'members' | 'invite'
  | 'revokeInvitation' | 'checkOpenItems' | 'imports' | 'importBatch' | 'setAccountMap' | 'validateImport' | 'commitImport' | 'reverseImport' | 'compareWithSage'
  | 'uploadImport' | 'discardImport' | 'sageStatus' | 'sageConnect' | 'sageDisconnect' | 'sagePull' | 'backups' | 'requestBackup'
  | 'downloadBackup' | 'restoreBackup'>;

const delay = <T,>(v: T, ms = 80): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (detail: string) => Promise.reject(new Error(detail));
export const DEMO_TODAY = '2026-10-11';
const ORG_NAME = 'Blaze Fire Systems Ltd';

// ---- VAT (two-monthly, invoice basis) ---------------------------------------------
interface Filing { from: string; to: string; reference: string; filed_at: string; boxes?: Record<string, string> }
let filings: Filing[] = [];
const PERIODS: [string, string, string][] = [
  ['2026-01-01', '2026-02-28', 'Jan–Feb'], ['2026-03-01', '2026-04-30', 'Mar–Apr'], ['2026-05-01', '2026-06-30', 'May–Jun'],
  ['2026-07-01', '2026-08-31', 'Jul–Aug'], ['2026-09-01', '2026-10-31', 'Sep–Oct'],
];

function vatBoxes(from: string, to: string): Record<string, string> {
  const inP = demoStore.all().filter((d) => d.status !== 'draft' && d.status !== 'void' && d.issue_date >= from && d.issue_date <= to);
  const T1 = addMoney('0', ...inP.filter((d) => d.type === 'sales_invoice').map((d) => d.tax_total),
    ...inP.filter((d) => d.type === 'sales_credit').map((d) => negate(d.tax_total)));
  const T2 = addMoney('0', ...inP.filter((d) => d.type === 'purchase_bill').map((d) => d.tax_total),
    ...inP.filter((d) => d.type === 'purchase_credit').map((d) => negate(d.tax_total)));
  const net = addMoney(T1, negate(T2));
  const pos = compareMoney(net, '0') >= 0;
  return { T1, T2, T3: pos ? net : '0.0000', T4: pos ? '0.0000' : negate(net), E1: '0.0000', E2: '0.0000', ES1: '0.0000', ES2: '0.0000' };
}

function vatReturn([from, to, label]: [string, string, string]): VatReturn {
  const f = filings.find((x) => x.from === from);
  const boxes = f?.boxes ?? vatBoxes(from, to);
  const end = new Date(`${to}T12:00:00Z`);
  const due = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 19)).toISOString().slice(0, 10);
  const rosDue = due.slice(0, 8) + '23';
  const status: VatReturn['status'] = f ? 'filed' : rosDue < DEMO_TODAY ? 'overdue' : to < DEMO_TODAY ? 'due' : 'open';
  return {
    id: f ? `v-${from}` : null, type: 'IE_VAT3', from, to, label, basis: 'invoice', status, due_date: due, ros_due_date: rosDue,
    boxes, net_payable: addMoney(boxes.T3!, negate(boxes.T4!)), filed_at: f?.filed_at ?? null, reference: f?.reference ?? null,
  };
}

const whole = (v: string) => Number((toUnits(v) + (toUnits(v) >= 0n ? 5000n : -5000n)) / 10000n);

// ---- Year end ---------------------------------------------------------------------------
let years: FiscalYear[] = [];
const monthLabel = (iso: string) => new Intl.DateTimeFormat('en-IE', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`));
const shortDay = (iso: string) => new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`));
/** Irish VAT is due on the 23rd of the month after the period (via ROS). */
function vatDue(periodEnd: string) {
  const d = new Date(`${periodEnd}T12:00:00Z`);
  const due = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 23));
  return new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(due);
}

function months(year: number, status: (m: number) => Period['status'], reason: (m: number) => string | null): Period[] {
  return Array.from({ length: 12 }, (_, m) => {
    const start = new Date(Date.UTC(year, m, 1)).toISOString().slice(0, 10);
    const end = new Date(Date.UTC(year, m + 1, 0)).toISOString().slice(0, 10);
    return { id: `p${year}-${m + 1}`, starts_on: start, ends_on: end, status: status(m), locked_reason: reason(m) };
  });
}

// ---- Recurring, statements -----------------------------------------------------------------
let templates: RecurringTemplate[] = [];
let runs: (RecurringRun & { send?: boolean })[] = [];
let schedules: StatementSchedule[] = [];

// ---- Users -------------------------------------------------------------------------------
let memberList: MemberList;

// ---- Import --------------------------------------------------------------------------------
let batches: ImportBatch[] = [];
let opening: OpeningBalanceStatus;
let sage: SageStatus;

// ---- Backups ---------------------------------------------------------------------------------
let backupList: (Backup & { pendingUntilListed?: boolean })[] = [];

let seq = 0;

function seed() {
  seq = 0;
  filings = [
    { from: '2026-01-01', to: '2026-02-28', reference: 'ROS-4471029', filed_at: '2026-03-20T10:12:00Z', boxes: { T1: '6120.4500', T2: '2310.2000', T3: '3810.2500', T4: '0.0000', E1: '0.0000', E2: '0.0000', ES1: '0.0000', ES2: '0.0000' } },
    { from: '2026-03-01', to: '2026-04-30', reference: 'ROS-4528810', filed_at: '2026-05-21T09:40:00Z', boxes: { T1: '7344.1000', T2: '2981.6500', T3: '4362.4500', T4: '0.0000', E1: '0.0000', E2: '0.0000', ES1: '0.0000', ES2: '0.0000' } },
    { from: '2026-05-01', to: '2026-06-30', reference: 'ROS-4602211', filed_at: '2026-07-20T16:05:00Z', boxes: { T1: '6890.0000', T2: '3105.4000', T3: '3784.6000', T4: '0.0000', E1: '0.0000', E2: '0.0000', ES1: '0.0000', ES2: '0.0000' } },
  ];
  years = [
    { id: 'fy2026', starts_on: '2026-01-01', ends_on: '2026-12-31', status: 'open', closing_journal_id: null,
      periods: months(2026, (m) => (m < 6 ? 'locked' : 'open'), (m) => (m < 6 ? 'VAT return filed' : null)) },
    { id: 'fy2025', starts_on: '2025-01-01', ends_on: '2025-12-31', status: 'closed', closing_journal_id: 'j-close-2025',
      periods: months(2025, () => 'locked', () => 'Year closed') },
  ];

  templates = [
    { id: 't-mon', name: 'Monitoring — Swords Pavilions', doc_type: 'sales_invoice', contact_id: 'c1', currency: 'EUR', reference: 'Monitoring {{month}} {{year}}',
      lines: [{ description: 'Alarm monitoring {{month}}', quantity: '1', unit_price: '85.00', account_code: '4020', tax_code: 'R135' }],
      due_days: 30, frequency: 'monthly', interval_count: 1, day_rule: 'same_day', start_date: '2026-01-09', next_run_date: '2026-11-09',
      mode: 'approve', email_to: ['accounts@swordspavilions.ie'], status: 'active' },
    { id: 't-dhg', name: 'Service contract — Dublin Hotels Group', doc_type: 'sales_invoice', contact_id: 'c4', currency: 'EUR', reference: 'Q{{period_start}}',
      lines: [{ description: 'Quarterly fire alarm service — 3 hotels', quantity: '3', unit_price: '264.32', account_code: '4020', tax_code: 'R135' }],
      due_days: 30, frequency: 'quarterly', interval_count: 1, day_rule: 'month_end', start_date: '2026-03-31', next_run_date: '2026-12-31',
      mode: 'auto_post_send', email_to: ['finance@dublinhotels.ie'], status: 'active' },
    { id: 't-eir', name: 'Eir broadband', doc_type: 'purchase_bill', contact_id: 's2', currency: 'EUR',
      lines: [{ description: 'Business broadband', quantity: '1', unit_price: '59.00', account_code: '7300', tax_code: 'S23' }],
      due_days: 14, frequency: 'monthly', interval_count: 1, day_rule: 'same_day', start_date: '2026-01-26', next_run_date: '2026-10-26',
      mode: 'auto_post', email_to: [], status: 'paused' },
  ];
  // The November monitoring draft already in the demo invoices, and one more waiting.
  const monitoring = demoStore.all().find((d) => d.status === 'draft' && d.contact_id === 'c1');
  const tallaght = demoStore.createDraft({ type: 'sales_invoice', contact_id: 'c7', issue_date: '2026-10-10', due_date: '2026-11-09',
    lines: [{ description: 'Emergency lighting monthly check — October', quantity: '1', unit_price: '45.00', account_code: '4020', tax_code: 'R135' }] });
  templates.push({ id: 't-tmc', name: 'Lighting checks — Tallaght Medical Centre', doc_type: 'sales_invoice', contact_id: 'c7', currency: 'EUR',
    lines: [{ description: 'Emergency lighting monthly check — {{month}}', quantity: '1', unit_price: '45.00', account_code: '4020', tax_code: 'R135' }],
    due_days: 30, frequency: 'monthly', interval_count: 1, day_rule: 'same_day', start_date: '2026-02-10', next_run_date: '2026-11-10',
    mode: 'approve', email_to: ['practice@tallaghtmc.ie'], status: 'active' });
  runs = [
    { id: 'run1', template_id: 't-mon', template_name: 'Monitoring — Swords Pavilions', run_date: '2026-10-09', status: 'awaiting_approval',
      document_id: monitoring?.id ?? null, gross_total: monitoring?.gross_total ?? '96.4800', decided_by: null, note: null, contact_name: 'Swords Pavilions', doc_type: 'sales_invoice' },
    { id: 'run2', template_id: 't-tmc', template_name: 'Lighting checks — Tallaght Medical Centre', run_date: '2026-10-10', status: 'awaiting_approval',
      document_id: tallaght, gross_total: demoStore.get(tallaght)!.gross_total, decided_by: null, note: null, contact_name: 'Tallaght Medical Centre', doc_type: 'sales_invoice' },
    { id: 'run3', template_id: 't-dhg', template_name: 'Service contract — Dublin Hotels Group', run_date: '2026-09-30', status: 'posted',
      document_id: null, gross_total: '900.0100', decided_by: 'automatic', note: null, contact_name: 'Dublin Hotels Group', doc_type: 'sales_invoice' },
  ];
  schedules = [{ id: 'ss1', name: 'Month-end statements', frequency: 'monthly', day_of_month: 0, weekday: null, type: 'open_items',
    only_overdue: false, min_balance: '0.0100', contact_filter: 'with_balance', next_run_date: '2026-10-31', is_active: true }];

  memberList = {
    members: [
      { subject: 'ledger:user:brian', name: 'Brian', email: 'brian@blazefire.ie', role: 'owner', firm: null, access_ends_at: null, billable: true, last_seen_at: '2026-10-11T09:12:00Z' },
      { subject: 'ledger:user:aoife', name: 'Aoife Byrne', email: 'aoife@blazefire.ie', role: 'bookkeeper', firm: null, access_ends_at: null, billable: true, last_seen_at: '2026-10-09T15:40:00Z' },
      { subject: 'ledger:user:declan', name: 'Declan Walsh', email: 'declan@walshaccountants.ie', role: 'accountant', firm: 'Walsh & Co Accountants', access_ends_at: '2027-03-31T23:59:59Z', billable: false, last_seen_at: '2026-09-02T11:05:00Z' },
    ],
    invitations: [{ id: 'inv1', email: 'office@blazefire.ie', role: 'viewer', expires_at: '2026-10-18T09:00:00Z' }],
    billable_seats: 2,
  };

  batches = [];
  opening = { as_at: null, control_balance: '0.0000', balanced: true };
  sage = { connected: false, business_name: null, connected_at: null, expires_at: null, last_pulled_at: null };

  const day = (d: number) => new Date(Date.UTC(2026, 9, d, 2, 10)).toISOString();
  backupList = [
    ...[11, 10, 9, 8, 7].map((d, i): Backup => ({ id: `bk${d}`, kind: 'scheduled', status: 'complete', created_at: day(d), data_as_at: day(d),
      size_bytes: 1_840_000 - i * 9_000, schema_version: 11, restore_modes: ['copy', 'replace'], expires_at: new Date(Date.UTC(2026, 10, d)).toISOString(), reason: null })),
    { id: 'bk-man', kind: 'manual', status: 'complete', created_at: '2026-10-02T17:30:00Z', data_as_at: '2026-10-02T17:30:00Z', size_bytes: 1_790_000,
      schema_version: 11, restore_modes: ['copy', 'replace'], expires_at: '2027-10-03T00:00:00Z', reason: 'Before tidying contacts' },
    { id: 'bk-vat', kind: 'pre_vat_filing', status: 'complete', created_at: '2026-07-20T16:00:00Z', data_as_at: '2026-07-20T16:00:00Z', size_bytes: 1_520_000,
      schema_version: 10, restore_modes: ['copy'], expires_at: '2027-07-21T00:00:00Z', reason: 'Before filing May–Jun VAT' },
  ];
}
seed();

/** Recurring runs waiting for approval (for the menu count). */
export function runsAwaitingApproval() {
  return runs.filter((r) => r.status === 'awaiting_approval').length;
}

export function resetDemoSettings() {
  seed();
}

function settle(id: string): Promise<RecurringRun> {
  const { send: _s, ...run } = runs.find((r) => r.id === id)!;
  return delay(run);
}

function batch(entity: ImportEntity, rows: number, extra: Partial<ImportBatch> = {}): ImportBatch {
  return { id: `ib${++seq}`, entity, source_format: 'sage_accounting', status: 'staged', row_count: rows, error_count: 0, errors: [], account_map: [], committed_at: null, ...extra };
}

/** Trial balance lines from the CSV lines after the headings: code, name, debit, credit (quotes and thousands commas allowed). */
function tbLines(lines: string[]): { code: string; name: string; debit: string; credit: string }[] {
  const cells = (l: string) => (l.match(/("([^"]|"")*"|[^,]*)(,|$)/g) ?? []).map((c) => c.replace(/,$/, '').replace(/^"|"$/g, '').replace(/""/g, '"'));
  const amount = (v = '') => { const n = Number(v.replace(/[^0-9.-]/g, '')); return Number.isFinite(n) && n ? n.toFixed(2) : '0.00'; };
  return lines.map(cells).filter((r) => (r[0] ?? '').trim() && !/^total/i.test((r[0] ?? '').trim()))
    .map((r) => ({ code: r[0]!.trim(), name: (r[1] ?? '').trim(), debit: amount(r[2]), credit: amount(r[3]) }))
    .filter((r) => r.debit !== '0.00' || r.credit !== '0.00');
}

export const demoSettings: SettingsSource = {
  // ---- VAT
  vatReturns: () => delay(PERIODS.map(vatReturn).reverse()),
  calculateVat: (from) => {
    const p = PERIODS.find((x) => x[0] === from);
    return p ? delay(vatReturn(p)) : fail('That isn’t one of your VAT periods.');
  },
  rosVat3: (from, to) => {
    const p = PERIODS.find((x) => x[0] === from);
    if (!p) return fail('That isn’t one of your VAT periods.');
    const b = vatReturn(p).boxes;
    const w = (k: string) => whole(b[k] ?? '0');
    const T1 = w('T1'), T2 = w('T2');
    const r: RosVat3 = { from, to, T1, T2, T3: Math.max(T1 - T2, 0), T4: Math.max(T2 - T1, 0), PA1: 0, E1: w('E1'), E2: w('E2'), ES1: w('ES1'), ES2: w('ES2') };
    return delay(r);
  },
  rosVat3Csv: () => Promise.resolve(null),
  fileVat: (from, to, reference) => {
    const p = PERIODS.find((x) => x[0] === from && x[1] === to);
    if (!p) return fail('That isn’t one of your VAT periods.');
    if (to >= DEMO_TODAY) return fail(`The period doesn’t end until ${to}; file it after that.`);
    if (filings.some((f) => f.from === from)) return fail('That return has already been filed.');
    if (!reference.trim()) return fail('Enter the ROS acknowledgement number.');
    filings.push({ from, to, reference: reference.trim(), filed_at: new Date().toISOString(), boxes: vatBoxes(from, to) });
    const fy = years.find((y) => y.id === 'fy2026')!;
    fy.periods = fy.periods.map((m) => (m.starts_on >= from && m.ends_on <= to ? { ...m, status: 'locked', locked_reason: 'VAT return filed' } : m));
    backupList = backupList.map((b) => (b.data_as_at && b.data_as_at < new Date().toISOString() ? { ...b, restore_modes: ['copy'] } : b));
    return delay(vatReturn(p));
  },

  // ---- Year end
  fiscalYears: () => delay(years),
  yearEndChecklist: (id) => {
    const y = years.find((x) => x.id === id);
    if (!y) return fail('That year doesn’t exist.');
    if (y.status === 'closed') return delay<ChecklistItem[]>([{ key: 'closed', label: 'Year closed', state: 'ok', detail: 'Closing journal posted' }]);
    const drafts = demoStore.all().filter((d) => d.status === 'draft' && d.issue_date >= y.starts_on && d.issue_date <= y.ends_on).length;
    const bank = unmatchedBankLines(y.ends_on);
    const vatMonths = y.periods.filter((p) => p.status !== 'locked').length;
    const waiting = runs.filter((r) => r.status === 'awaiting_approval').length;
    const inbox = openInboxCount();
    const item = (key: string, label: string, bad: boolean, severity: 'warning' | 'blocking', detail: string): ChecklistItem =>
      ({ key, label, state: bad ? severity : 'ok', detail });
    return delay<ChecklistItem[]>([
      item('drafts', 'Draft documents', drafts > 0, 'warning', `${drafts} draft document(s) dated in the year`),
      item('bank', 'Bank reconciled to the year end', bank > 0, 'blocking', `${bank} bank line(s) up to ${y.ends_on} still to reconcile`),
      item('suspense', 'Suspense account cleared', false, 'blocking', 'Suspense balance 0.00'),
      item('opening_control', 'Opening balances agree', !opening.balanced, 'blocking', `Opening balances control ${opening.control_balance}`),
      item('vat', 'VAT returns filed for the year', vatMonths > 0, 'warning', `${vatMonths} month(s) not yet covered by a filed VAT return`),
      item('recurring', 'Recurring runs decided', waiting > 0, 'warning', `${waiting} recurring run(s) still waiting for approval`),
      item('inbox', 'Bill inbox dealt with', inbox > 0, 'warning', `${inbox} bill(s) in the inbox not dealt with`),
    ]);
  },
  closeYear: async (id, ack) => {
    const y = years.find((x) => x.id === id);
    if (!y) return fail('That year doesn’t exist.');
    if (y.status === 'closed') return fail('That year is already closed.');
    if (y.ends_on >= DEMO_TODAY) return fail(`The year doesn’t end until ${y.ends_on}.`);
    const checks = await demoSettings.yearEndChecklist(id);
    const blocking = checks.filter((c) => c.state === 'blocking');
    if (blocking.length) return fail(`Can’t close yet: ${blocking.map((c) => c.detail).join('; ')}`);
    if (!ack && checks.some((c) => c.state === 'warning')) return fail('There are warnings on the checklist. Confirm to close anyway.');
    y.status = 'closed';
    y.closing_journal_id = `j-close-${++seq}`;
    y.periods = y.periods.map((p) => ({ ...p, status: 'locked', locked_reason: 'Year closed' }));
    return delay(y);
  },
  reopenYear: (id, reason) => {
    const y = years.find((x) => x.id === id);
    if (!y || y.status !== 'closed') return fail('Only a closed year can be reopened.');
    if (reason.trim().length < 3) return fail('Give a reason (at least three characters).');
    y.status = 'open';
    y.closing_journal_id = null;
    y.periods = y.periods.map((p) => (p.locked_reason === 'Year closed' ? { ...p, status: 'closed', locked_reason: null } : p));
    return delay(y);
  },
  setPeriodStatus: (id, status) => {
    const y = years.find((x) => x.periods.some((p) => p.id === id));
    const p = y?.periods.find((x) => x.id === id);
    if (!y || !p) return fail('That period doesn’t exist.');
    if (p.status === 'locked') return fail(`${p.locked_reason ?? 'Locked'}; it can’t be reopened here.`);
    if (status === 'locked') return fail('Periods are locked by filing VAT or closing the year.');
    if (p.status !== status) {
      recordDemoAudit({ kind: 'period', action: status === 'open' ? 'reopened' : 'closed', label: monthLabel(p.starts_on),
        entity_id: p.id, changes: { status: { from: p.status, to: status } } });
    }
    p.status = status;
    return delay(p);
  },

  // ---- Month end (as ledger.month_end_checklist / close_period)
  monthEndChecklist: (id) => {
    const p = years.flatMap((y) => y.periods).find((x) => x.id === id);
    if (!p) return fail('There’s no such month.');
    const end = shortDay(p.ends_on);
    const earlier = years.flatMap((y) => y.periods).filter((x) => x.ends_on < p.starts_on && x.status === 'open')
      .sort((a, b) => a.starts_on.localeCompare(b.starts_on)).map((x) => monthLabel(x.starts_on));
    const bank = unmatchedBankLines(p.ends_on);
    const inbox = openInboxCount(p.ends_on);
    const drafts = demoStore.all().filter((d) => d.status === 'draft' && d.issue_date >= p.starts_on && d.issue_date <= p.ends_on).length;
    const vp = PERIODS.find(([from, to]) => from <= p.ends_on && p.ends_on <= to);
    const waiting = runs.filter((r) => r.status === 'awaiting_approval' && r.run_date <= p.ends_on).length;
    const item = (key: string, label: string, open: boolean, detail: string): ChecklistItem =>
      ({ key, label, state: open ? 'warning' : 'ok', detail });
    const vat = !vp ? item('vat', 'VAT return', false, 'No VAT period set up for this month')
      : vp[1] > p.ends_on ? item('vat', 'VAT return', false, `The VAT period ${vp[2]} runs to ${shortDay(vp[1])}`)
        : filings.some((f) => f.from === vp[0]) ? item('vat', 'VAT return', false, `VAT return for ${vp[2]} filed`)
          : item('vat', 'VAT return', true, `VAT return for ${vp[2]} isn’t filed yet (due ${vatDue(vp[1])})`);
    return delay<ChecklistItem[]>([
      item('earlier', 'Earlier months closed', earlier.length > 0,
        earlier.length === 0 ? 'Every earlier month is closed' : `${earlier.join(', ')} ${earlier.length === 1 ? 'is' : 'are'} still open`),
      item('bank', 'Bank reconciled to the month end', bank > 0,
        bank === 0 ? `Every bank account is reconciled to ${end}` : `AIB Current: ${bank} line(s) to match`),
      item('inbox', 'Bill inbox dealt with', inbox > 0, inbox === 0 ? 'Nothing waiting in the bill inbox' : `${inbox} bill(s) in the inbox still to deal with`),
      item('drafts', 'No drafts dated in the month', drafts > 0, drafts === 0 ? 'No drafts dated in the month' : `${drafts} draft(s) dated in the month`),
      vat,
      item('suspense', 'Suspense account cleared', false, 'Nothing in suspense'),
      item('recurring', 'Recurring runs decided', waiting > 0, waiting === 0 ? 'No recurring runs waiting' : `${waiting} recurring run(s) waiting for approval`),
    ]);
  },
  closePeriod: async (id, ack) => {
    const p = years.flatMap((y) => y.periods).find((x) => x.id === id);
    if (!p) return fail('There’s no such month.');
    if (p.status === 'locked') return fail(`${monthLabel(p.starts_on)} is locked (${p.locked_reason ?? 'filed'})`);
    if (p.status === 'closed') return fail(`${monthLabel(p.starts_on)} is already closed`);
    const open = (await demoSettings.monthEndChecklist(id)).filter((c) => c.state !== 'ok');
    if (open.length && !ack) return fail(`Month end for ${monthLabel(p.starts_on)} has items to confirm: ${open.map((c) => c.detail).join('; ')}`);
    p.status = 'closed';
    recordDemoAudit({ kind: 'period', action: 'closed', label: monthLabel(p.starts_on), entity_id: p.id, changes: { status: { from: 'open', to: 'closed' } } });
    return delay(p);
  },

  // ---- Statements
  createStatements: (req) => {
    const customers = demoStore.contacts.filter((c) => c.is_customer && req.contact_ids.includes(c.id));
    const min = req.min_balance ?? '0.01';
    const open = demoStore.open();
    const chosen = customers.filter((c) => {
      const mine = open.filter((d) => d.contact_id === c.id && d.type.startsWith('sales'));
      const balance = addMoney('0', ...mine.map((d) => (d.type === 'sales_credit' ? negate(d.outstanding) : d.outstanding)));
      if (compareMoney(balance, min) < 0) return false;
      return !req.only_overdue || mine.some((d) => d.type === 'sales_invoice' && d.due_date && d.due_date < req.as_at);
    });
    const noEmail = chosen.filter((c) => !c.email).map((c) => c.name);
    return delay({ created: chosen.length, emailed: req.send ? chosen.length - noEmail.length : 0, skipped: customers.length - chosen.length, no_email: req.send ? noEmail : [] });
  },
  statementSchedules: () => delay(schedules),
  createStatementSchedule: (s) => {
    if (!s.name.trim()) return fail('Name the schedule.');
    const day = s.frequency === 'monthly' ? (s.day_of_month ?? 0) : null;
    const next = s.frequency === 'monthly'
      ? (day === 0 ? '2026-10-31' : `2026-${day! >= 11 ? '10' : '11'}-${String(day).padStart(2, '0')}`)
      : '2026-10-12';
    const saved: StatementSchedule = { ...s, id: `ss${++seq}`, day_of_month: day, weekday: s.frequency === 'weekly' ? (s.weekday ?? 1) : null,
      contact_filter: 'with_balance', next_run_date: next, is_active: true, min_balance: s.min_balance ?? '0.0100' };
    schedules = [...schedules, saved];
    return delay(saved);
  },

  // ---- Recurring
  recurring: () => delay(templates),
  createRecurring: (t) => {
    if (!t.name.trim()) return fail('Name the template.');
    if (!demoStore.contacts.some((c) => c.id === t.contact_id)) return fail('Choose who it’s for.');
    const bad = [t.reference ?? '', ...t.lines.map((l) => l.description)].join(' ').match(/\{\{\s*([a-z_.]+)\s*\}\}/g)
      ?.map((m) => m.replace(/[{}\s]/g, '')).filter((f) => !['month', 'year', 'period_start', 'period_end'].includes(f));
    if (bad?.length) return fail(`Unknown merge field ${bad.join(', ')}`);
    const saved: RecurringTemplate = { ...t, id: `t${++seq}`, next_run_date: t.start_date, status: 'active', mode: t.mode ?? 'approve', email_to: t.email_to ?? [] };
    templates = [...templates, saved];
    return delay(saved);
  },
  runRecurringNow: (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t || t.status !== 'active') return fail('Only an active template can run.');
    const input: DocumentInput = { type: t.doc_type, contact_id: t.contact_id, issue_date: DEMO_TODAY,
      lines: t.lines.map((l) => ({ ...l, description: l.description.replace(/\{\{\s*month\s*\}\}/g, 'October').replace(/\{\{\s*year\s*\}\}/g, '2026') })) };
    const doc = demoStore.createDraft(input);
    const run: RecurringRun = { id: `run${++seq}`, template_id: t.id!, template_name: t.name, run_date: DEMO_TODAY, status: 'awaiting_approval',
      document_id: doc, gross_total: demoStore.get(doc)!.gross_total, decided_by: null, note: null,
      contact_name: demoStore.contacts.find((c) => c.id === t.contact_id)?.name ?? '', doc_type: t.doc_type };
    runs = [run, ...runs];
    return delay(run);
  },
  recurringRuns: (status) => delay(runs.filter((r) => !status || r.status === status).map(({ send: _s, ...r }) => r)),
  approveRun: (id, send) => {
    const r = runs.find((x) => x.id === id);
    if (!r || r.status !== 'awaiting_approval' || !r.document_id) return fail('That run isn’t waiting for approval.');
    if (send && r.doc_type !== 'sales_invoice') return fail('Only sales invoices can be emailed.');
    demoStore.post(r.document_id);
    Object.assign(r, { status: 'posted', decided_by: 'ledger:user:brian', note: send ? 'Posted and emailed' : 'Posted', send });
    return settle(id);
  },
  declineRun: (id, skip, note) => {
    const r = runs.find((x) => x.id === id);
    if (!r || r.status !== 'awaiting_approval') return fail('That run isn’t waiting for a decision.');
    if (r.document_id) demoStore.remove(r.document_id);
    Object.assign(r, { status: skip ? 'skipped' : 'rejected', decided_by: 'ledger:user:brian', note: note ?? null, document_id: null });
    return settle(id);
  },

  // ---- Users
  members: () => delay(memberList),
  invite: (inv) => {
    const email = inv.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return fail('That doesn’t look like an email address.');
    if (memberList.members.some((m) => m.email === email)) return fail(`${email} already has access.`);
    if (memberList.invitations.some((i) => i.email === email)) return fail(`${email} has already been invited.`);
    if (inv.role === 'owner') return fail('There can only be one owner. Invite them as an admin.');
    const id = `inv${++seq}`;
    memberList.invitations = [...memberList.invitations, { id, email, role: inv.role, expires_at: '2026-10-18T12:00:00Z' }];
    return delay({ invitation_id: id, email_sent: true, accept_url: null });
  },
  revokeInvitation: async (id) => {
    if (!memberList.invitations.some((i) => i.id === id)) return fail('That invitation no longer exists.');
    memberList.invitations = memberList.invitations.filter((i) => i.id !== id);
    await delay(null);
  },

  // ---- Import
  imports: () => delay({ batches: [...batches].reverse(), opening_balances: opening }),
  importBatch: (id) => {
    const b = batches.find((x) => x.id === id);
    // As the worker: a backup asked for before an import is taken by the next look.
    if (b?.backup_status === 'pending') b.backup_status = 'completed';
    return b ? delay(b) : fail('That import no longer exists.');
  },
  setAccountMap: (id, map: AccountMapEntry[]) => {
    const b = batches.find((x) => x.id === id);
    if (!b || b.status === 'completed' || b.status === 'reversed') return fail('That import can’t be changed now.');
    b.account_map = b.account_map!.map((e) => ({ ...e, target_code: map.find((m) => m.source_code === e.source_code)?.target_code ?? e.target_code }));
    b.status = 'staged';
    return delay(b);
  },
  validateImport: (id) => {
    const b = batches.find((x) => x.id === id);
    if (!b) return fail('That import no longer exists.');
    const errors: NonNullable<ImportBatch['errors']> = [];
    b.account_map?.forEach((e, i) => {
      if (!e.target_code) errors.push({ row: i + 1, field: 'account', message: `Choose an account for ${e.source_code} ${e.source_name ?? ''}`.trim() });
      else if (!demoStore.accounts.some((a) => a.code === e.target_code)) errors.push({ row: i + 1, field: 'account', message: `Account ${e.target_code} doesn’t exist` });
    });
    if (b.entity === 'purchase_invoices' && !batches.some((x) => x.entity === 'suppliers' && x.status === 'completed')) {
      errors.push({ row: 3, field: 'contact_code', message: 'Supplier PLUMBC isn’t in the books yet. Import suppliers first, or let this import create them.' });
    }
    // As the API (#35): each trial balance line with the account it goes to and its problem.
    if (b.rows) {
      b.rows = b.rows.map((r) => {
        const target = b.account_map?.find((e) => e.source_code === r.source_code)?.target_code || null;
        const acc = demoStore.accounts.find((a) => a.code === target);
        const problem = !target ? `Choose an account here for ${r.source_code}${r.source_name ? ` ${r.source_name}` : ''}` : !acc ? `Unknown account ${target}` : null;
        return { ...r, account_code: target, account_name: acc?.name ?? null, status: problem ? 'error' : 'ok', problem };
      });
      Object.assign(b, { errors: [], error_count: b.rows.filter((r) => r.problem).length, status: 'validated' });
      return delay(b);
    }
    Object.assign(b, { errors, error_count: errors.length, status: 'validated' });
    return delay(b);
  },
  commitImport: (id, create) => {
    const b = batches.find((x) => x.id === id);
    if (!b || b.status !== 'validated') return fail('Check the import first.');
    const blocking = (b.errors ?? []).filter((e) => !(create && e.field === 'contact_code'));
    if (blocking.length) return fail(`Fix the ${blocking.length} problem(s) first.`);
    // As the API: the first call asks for a backup and comes back unimported; call again once it's completed.
    if (!b.pre_import_backup_id || b.backup_status === 'failed') {
      Object.assign(b, { pre_import_backup_id: `bk${++seq}`, backup_status: 'pending' });
      return delay(b);
    }
    if (b.backup_status !== 'completed') return delay(b);
    Object.assign(b, { status: 'completed', committed_at: new Date().toISOString(), errors: [], error_count: 0 });
    if (b.entity === 'opening_balances') opening = { as_at: sage.last_pulled_at?.slice(0, 10) ?? '2026-09-30', control_balance: '0.0000', balanced: true };
    backupList = [{ id: `bk${++seq}`, kind: 'pre_import', status: 'complete', created_at: new Date().toISOString(), data_as_at: new Date().toISOString(),
      size_bytes: 1_850_000, schema_version: 11, restore_modes: ['copy', 'replace'], expires_at: '2027-10-12T00:00:00Z', reason: `Before importing ${b.entity.replace('_', ' ')}` }, ...backupList];
    return delay(b);
  },
  // As ledger.compare_with_sage (db/054). The demo's books agree with the trial balance it imported; a customer or
  // supplier in Sage's aged report that the demo hasn't got shows as a difference.
  compareWithSage: async (id, input) => {
    const b = batches.find((x) => x.id === id);
    if (!b || b.entity !== 'opening_balances') return fail('Compare an imported trial balance');
    if (b.status !== 'completed') return fail('Import the trial balance first, then compare');
    const carry = b.profit_to_capital !== false;
    const target = (code: string) => (b.account_map ?? []).find((m) => m.source_code === code)?.target_code || code;
    const byCode = new Map<string, { name: string; amount: number; codes: { code: string; name: string | null }[] }>();
    for (const r of b.rows ?? []) {
      const code = carry && /^\d{4}$/.test(r.source_code ?? '') && r.source_code! >= '4000' && r.source_code! < '9000' ? '3000' : target(r.source_code ?? '');
      const line = byCode.get(code) ?? { name: code === '3000' && carry ? 'Capital' : r.source_name ?? code, amount: 0, codes: [] };
      line.amount += Number(r.debit ?? 0) - Number(r.credit ?? 0);
      line.codes.push({ code: r.source_code ?? '', name: r.source_name ?? null });
      byCode.set(code, line);
    }
    const lines = [...byCode.entries()].filter(([, l]) => Math.abs(l.amount) > 0.004).sort(([a], [z]) => a.localeCompare(z)).map(([code, l]) => ({
      code, name: l.name, sage_codes: l.codes, sage: l.amount.toFixed(2), ledger: l.amount.toFixed(2), difference: '0.00', same: true }));
    const sum = (f: (n: number) => boolean) => lines.reduce((t, l) => (f(Number(l.sage)) ? t + Math.abs(Number(l.sage)) : t), 0).toFixed(2);
    const control = (kind: 'debtors' | 'creditors') => lines.filter((l) => l.code === (kind === 'debtors' ? '1100' : '2100'))
      .reduce((t, l) => t + (kind === 'debtors' ? 1 : -1) * Number(l.sage), 0);
    const aged = async (kind: 'debtors' | 'creditors', rows?: SageCompareRow[]): Promise<SageCompareAged | null> => {
      if (!rows) return null;
      const people = await demoDocuments.contacts(kind === 'debtors' ? 'customer' : 'supplier');
      const groups = new Map<string, { id: string | null; ref: string | null; name: string; amount: number }>();
      for (const r of rows) {
        const ref = (r.contact_ref ?? '').trim();
        const who = people.find((c) => ref && (c.code ?? '').toUpperCase() === ref.toUpperCase())
          ?? people.find((c) => r.contact_name && c.name.toLowerCase() === r.contact_name.trim().toLowerCase());
        const key = who?.id ?? `?${(r.contact_name || ref).toLowerCase()}`;
        const g = groups.get(key) ?? { id: who?.id ?? null, ref: ref || null, name: who?.name ?? r.contact_name ?? ref, amount: 0 };
        g.amount += r.type === 'credit' ? -Math.abs(Number(r.outstanding ?? 0)) : Number(r.outstanding ?? 0);
        groups.set(key, g);
      }
      const out = [...groups.values()].filter((g) => Math.abs(g.amount) > 0.004).map((g) => {
        const books = g.id ? g.amount : 0;
        return { contact_id: g.id, ref: g.ref, name: g.name, in_ledger: !!g.id, sage: g.amount.toFixed(2), ledger: books.toFixed(2),
          difference: (books - g.amount).toFixed(2), same: Math.abs(books - g.amount) < 0.005 };
      }).sort((a, z) => Number(a.same) - Number(z.same) || a.name.localeCompare(z.name));
      const sageTotal = out.reduce((t, l) => t + Number(l.sage), 0);
      return { lines: out, same: out.filter((l) => l.same).length, different: out.filter((l) => !l.same).length,
        sage_total: sageTotal.toFixed(2), ledger_total: out.reduce((t, l) => t + Number(l.ledger), 0).toFixed(2),
        sage_trial_balance: control(kind).toFixed(2), report_matches_trial_balance: Math.abs(sageTotal - control(kind)) < 0.005 };
    };
    const debtors = await aged('debtors', input.debtors);
    const creditors = await aged('creditors', input.creditors);
    const asAt = b.as_at ?? '2026-12-31';
    const goLive = new Date(Date.parse(asAt) + 86_400_000).toISOString().slice(0, 10);
    const result: SageCompare = {
      batch: b.id, as_at: asAt, go_live: goLive, carries_profit: carry, carried_to: carry ? '3000' : null,
      trial_balance: { lines, same: lines.length, different: 0, sage_debit: sum((n) => n > 0), sage_credit: sum((n) => n < 0),
        ledger_debit: sum((n) => n > 0), ledger_credit: sum((n) => n < 0) },
      debtors, creditors,
      all_same: [debtors, creditors].every((x) => !x || (x.different === 0 && x.report_matches_trial_balance)),
    };
    return delay(result);
  },
  reverseImport: (id, reason) => {
    const b = batches.find((x) => x.id === id);
    if (!b || b.status !== 'completed') return fail('Only a completed import can be reversed.');
    if (reason.trim().length < 3) return fail('Give a reason (at least three characters).');
    b.status = 'reversed';
    if (b.entity === 'opening_balances') opening = { as_at: null, control_balance: '0.0000', balanced: true };
    return delay(b);
  },
  discardImport: (id) => {
    const b = batches.find((x) => x.id === id);
    if (!b || (b.status !== 'staged' && b.status !== 'validated')) return fail('Only an import not yet imported can be discarded; reverse an imported one instead');
    batches = batches.filter((x) => x.id !== id);
    return delay(undefined);
  },
  uploadImport: async (file, entity, sourceFormat, asAt, profitToCapital) => {
    if (entity !== 'opening_balances') return fail('The demo takes the Sage trial balance CSV for opening balances.');
    const text = await file.text();
    // As SageTrialBalanceCsv: the lines after the headings (the row with Debit), each with a code in the first column.
    const lines = text.split(/\r?\n/);
    const head = Math.max(0, lines.findIndex((l) => /(^|,)"?debit"?(,|$)/i.test(l)));
    const rows = lines.slice(head + 1).map((r) => r.split(',').map((c) => c.replace(/^"|"$/g, '')))
      .filter((r) => r.length >= 2 && r[0]!.trim() && !/^total/i.test(r[0]!.trim()));
    if (!rows.length) return fail('No rows found. Export the trial balance from Sage as CSV, with the header row.');
    // As db/041: the same code and kind where it exists; Sage's income and cost codes (4000–8999) go to profit carried (3000).
    const same = (code: string) => (demoStore.accounts.some((a) => a.code === code) ? code : undefined); // only accounts the demo has
    // As db/045: income and costs go to capital only at a year end (here: an as-at date of 31 December).
    const carry = profitToCapital ?? (!asAt || asAt.endsWith('-12-31')); // asked on the screen; else the date decides
    const known = (code: string) => (carry && /^\d{4}$/.test(code) && code >= '4000' && code < '9000' ? '3000'
      : ['1200', '1210', '2201', '2202', ...(carry ? [] : ['4000', '4010', '7900'])].includes(code) ? same(code) : undefined);
    const b = batch('opening_balances', rows.length, {
      file_name: file.name, source_format: sourceFormat, as_at: asAt ?? null, profit_to_capital: carry,
      account_map: [...new Map(rows.map(([code, name]) => [code!.trim(), name])).entries()].map(([code, name]) => ({ source_code: code, source_name: name?.trim(), target_code: known(code) ?? '', suggested: !!known(code) })),
      // Each line as the API gives it (#35); amounts read loosely, as SageTrialBalanceCsv does.
      rows: tbLines(lines.slice(head + 1)).map((r, i) => ({ row: i + 1, source_code: r.code, source_name: r.name, account_code: null, account_name: null,
        debit: r.debit, credit: r.credit, status: 'pending', problem: null })),
    });
    batches.push(b);
    return delay(b);
  },
  // As ledger.check_open_items (db/040): each row checked, contacts found by code or name; staged as batches with apply.
  checkOpenItems: async (input) => {
    if (!input.as_at) return fail('Give the date the unpaid items are "as at" (the day before go-live, usually 31 December)');
    const sales = input.kind === 'sales';
    const people = await demoDocuments.contacts(sales ? 'customer' : 'supplier');
    const docs = demoStore.all();
    const seen = new Set<string>();
    const out: OpenItemsCheck['rows'] = [];
    let inv = 0, crd = 0, invSum = 0, crdSum = 0;
    for (const r of input.rows) {
      const notes: string[] = [];
      let action: 'add' | 'skip' | 'problem' = 'add';
      const type = r.type === 'credit' ? 'credit' : 'invoice';
      const owed = Math.abs(Number(r.outstanding ?? 'NaN'));
      const total = r.total == null ? null : Math.abs(Number(r.total));
      if (!r.number || !r.date) { action = 'problem'; notes.push('Needs a number and a date'); }
      else if (Number.isNaN(owed)) { action = 'problem'; notes.push('An amount can’t be read'); }
      else if (owed === 0) { action = 'skip'; notes.push('Nothing left to pay'); }
      else if (total !== null && owed > total + 1e-9) { action = 'problem'; notes.push('More is unpaid than the total'); }
      const key = `${type}:${r.number}`;
      if (action === 'add' && seen.has(key)) { action = 'skip'; notes.push('Repeated earlier in the file'); }
      const docType = sales ? (type === 'invoice' ? 'sales_invoice' : 'sales_credit') : (type === 'invoice' ? 'purchase_bill' : 'purchase_credit');
      if (action === 'add' && docs.some((d) => d.type === docType && d.doc_no === r.number)) { action = 'skip'; notes.push('Already in the books'); }
      const ref = (r.contact_ref ?? '').trim().toUpperCase();
      const who = people.find((c) => ref && (c.code ?? '').toUpperCase() === ref)
        ?? people.find((c) => r.contact_name && c.name.toLowerCase() === r.contact_name.trim().toLowerCase());
      if (!who && action === 'add') notes.push(`${sales ? 'Customer' : 'Supplier'} ${r.contact_name || r.contact_ref || '?'} isn’t in the books yet: import ${sales ? 'customers' : 'suppliers'} first, or tick "Create missing" when importing`);
      if (action === 'add') {
        seen.add(key);
        if (type === 'invoice') { inv++; invSum += owed; } else { crd++; crdSum += owed; }
      }
      out.push({ row: r.row, action, type, number: r.number ?? null, contact_id: who?.id ?? null,
        contact_name: who?.name ?? r.contact_name ?? r.contact_ref ?? null, outstanding: Number.isNaN(owed) ? null : owed.toFixed(2), notes });
    }
    const batchIds: string[] = [];
    if (input.apply) {
      if (out.some((x) => x.action === 'problem')) return fail('Some rows have problems; put them right in the file, or leave them out, then check again');
      if (inv + crd === 0) return fail('Nothing to import: every row is skipped');
      if (inv) { const b = batch(sales ? 'sales_invoices' : 'purchase_invoices', inv, { file_name: input.file_name ?? undefined, status: 'validated' }); batches.push(b); batchIds.push(b.id); }
      if (crd) { const b = batch(sales ? 'sales_credits' : 'purchase_credits', crd, { file_name: input.file_name ?? undefined, status: 'validated' }); batches.push(b); batchIds.push(b.id); }
    }
    return delay({ rows: out, invoices: inv, credits: crd, invoice_total: invSum.toFixed(2), credit_total: crdSum.toFixed(2),
      target: null, already_imported: null, difference_after: null, batches: batchIds });
  },
  sageStatus: () => delay(sage),
  sageConnect: () => {
    sage = { connected: true, business_name: ORG_NAME, connected_at: new Date().toISOString(), expires_at: '2026-11-11T00:00:00Z', last_pulled_at: null };
    return delay({ authorise_url: '' });
  },
  sageDisconnect: async () => {
    sage = { connected: false, business_name: null, connected_at: null, expires_at: null, last_pulled_at: null };
    await delay(null);
  },
  sagePull: (asAt) => {
    if (!sage.connected) return fail('Connect to Sage first.');
    if (asAt >= DEMO_TODAY) return fail('Choose a migration date in the past, usually the last day of a VAT period.');
    const made = [batch('customers', 8), batch('suppliers', 5), batch('sales_invoices', 6), batch('purchase_invoices', 3)];
    batches.push(...made);
    sage = { ...sage, last_pulled_at: `${asAt}T23:59:59Z` };
    return delay({ as_at: asAt, batches: made.map((b) => ({ entity: b.entity, rows: b.row_count, id: b.id })) });
  },

  // ---- Backups
  backups: () => {
    // A requested backup completes by the next time the list is read (the worker takes ~2 minutes).
    backupList = backupList.map((b) => (b.pendingUntilListed ? { ...b, pendingUntilListed: false }
      : b.status === 'queued' ? { ...b, status: 'complete', data_as_at: new Date().toISOString(), size_bytes: 1_852_000, restore_modes: ['copy', 'replace'] } : b));
    return delay(backupList.map(({ pendingUntilListed: _p, ...b }) => b));
  },
  requestBackup: (reason) => {
    const b = { id: `bk${++seq}`, kind: 'manual' as const, status: 'queued' as const, created_at: new Date().toISOString(), data_as_at: null,
      size_bytes: null, schema_version: 11, restore_modes: [], expires_at: null, reason: reason?.trim() || null, pendingUntilListed: true };
    backupList = [b, ...backupList];
    const { pendingUntilListed: _p, ...out } = b;
    return delay(out);
  },
  downloadBackup: () => Promise.resolve(null),
  restoreBackup: (id, choice) => {
    const b = backupList.find((x) => x.id === id);
    if (!b || b.status !== 'complete') return fail('That backup isn’t available to restore.');
    if (choice.mode === 'copy') {
      if (!choice.new_name.trim()) return fail('Name the copy.');
    } else {
      if (!b.restore_modes?.includes('replace')) return fail('This backup predates a VAT return you’ve filed; restore it as a copy instead.');
      if (choice.confirm_name !== ORG_NAME) return fail('Type the company name exactly to confirm.');
    }
    return delay({ job_id: `job${++seq}` });
  },
};

